import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css"; //if using mantine date picker features
import "mantine-react-table/styles.css"; //make sure MRT styles were imported in your app root (once)
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Controller, useForm as useFormReactHook } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  Users,
  Plus,
  Trash2,
  Upload,
  Download,
  UserPlus,
  AlertTriangle,
  Settings,
  ChevronDown,
  ChevronRight,
  Edit,
  GraduationCap,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { UnifiedLoadingSpinner } from "@/components/loading/UnifiedLoadingSpinner";
import { TabsContent } from "@/components/ui/tabs";
import {
  ActionIcon,
  Badge,
  createTheme,
  DirectionProvider,
  Divider,
  Flex,
  Group,
  Loader,
  MantineProvider,
  Modal,
  MultiSelect,
  Stack,
  Table,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  flexRender,
  MantineReactTable,
  MRT_ColumnDef,
  MRT_GlobalFilterTextInput,
  MRT_TableBodyCellValue,
  MRT_ToolbarAlertBanner,
  useMantineReactTable,
} from "mantine-react-table";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "@mantine/form";
import { se } from "date-fns/locale";
import { useDisclosure } from "@mantine/hooks";

interface teacherForm {
  full_name: string;
  email: string;
  password: string;
  sections: string[];
}

const Teachers = () => {
  const { user, userProfile } = useAuth();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [stages, setStages] = useState<{
    [key: string]: { value: string; label: string };
  }>({});
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const { settings } = useRealSchoolSettings();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingteacher, setEditingteacher] = useState<any>(null);
  const { toast } = useToast();
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const { schoolId: schooIdParam } = useParams();
  const schoolId = schooIdParam || userProfile?.school_id;
  const [opened, { open, close }] = useDisclosure(false);
  const generateSimplePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
    return Array.from({ length: 8 }, () =>
      chars.charAt(Math.floor(Math.random() * chars.length))
    ).join("");
  };

  const form = useFormReactHook<teacherForm>({
    defaultValues: {
      full_name: "",
      email: "",
      password: "",
      sections: [],
    },
  });
  // const editForm = useFormReactHook<teacherForm>();
  const {
    values,
    setValues,
    errors,
    isTouched,
    reset,
    getInputProps,
    setFieldValue,
    isValid,
    onSubmit: onMantineFormSubmit,
    key: formKey,
  } = useForm<{ sections: string[] }>({
    initialValues: { sections: [] },
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const onSubmit = async (data: teacherForm) => {
    try {
      const passwordToUse =
        typeof data.password === "string" && data.password.trim().length > 0
          ? data.password.trim()
          : generateSimplePassword();

      const res = await supabase.functions.invoke("create-user", {
        body: {
          ...data,
          password: passwordToUse,
          ...values,
          school_id: schoolId,
          role: "teacher",
          withoutInviteEmail: true,
        },
      });
      console.log(res);
      // console.log(error)

      const { error, data: result } = res;
      if (error) {
        const errorMessage = await error.context.json();

        console.error("[Teachers.create] create-user failed", {
          rawInvokeError: error,
          invokeErrorMessage: error.message,
          errorContext: errorMessage,
          requestPayload: {
            email: data.email,
            full_name: data.full_name,
            hasPassword: !!passwordToUse,
            passwordLength: passwordToUse?.length ?? 0,
            role: "teacher",
            school_id: schoolId,
          },
        });

        throw errorMessage;
      }
      if (result) {
        console.info("[Teachers.create] create-user success", {
          result,
          authUserId: result?.data?.user?.id,
          returnedPassword: result?.data?.password,
          returnedPasswordLength: result?.data?.password?.length ?? 0,
        });
        const createdPassword = result?.data?.password || passwordToUse;
        const submittedPassword = passwordToUse;
        const passwordMismatch = createdPassword !== submittedPassword;

        if (passwordMismatch) {
          console.error("[Teachers.create] Password mismatch detected", {
            submittedPasswordLength: submittedPassword.length,
            createdPasswordLength: createdPassword?.length ?? 0,
            submittedPassword,
            createdPassword,
            authUserId: result?.data?.user?.id,
          });
        }
        form.reset();
        setIsDialogOpen(false);
        fetchProfiles();
        toast({
          title: "تم إضافة المعلم بنجاح",
          description: passwordMismatch
            ? `تم إضافة ${data.full_name}. كلمة المرور الفعلية المحفوظة: ${createdPassword} (ليست نفس كلمة المرور المدخلة).`
            : `تم إضافة ${data.full_name} بنجاح. كلمة المرور: ${createdPassword}`,
          duration: 10000,
        });
      }
    } catch (error) {
      toast({
        title: "خطأ في عملية إنشاء معلم",
        description: error?.message || "لم نتمكن من إنشاء المعلم",
        variant: "destructive",
      });
    }
  };

  const fetchProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    const { data, error } = await supabase
      .from("profiles_with_user")
      .select("*")
      .eq("role", "teacher")
      .eq("school_id", schoolId);
    if (error) {
      // handle error (toast, etc.)
      setProfiles([]);
    } else {
      setProfiles(data || []);
    }
    setLoadingProfiles(false);
  }, [schoolId]);

  useEffect(() => {
    if (userProfile) fetchProfiles();
  }, [fetchProfiles, userProfile]);
  useEffect(() => {
    if (settings) {
      const tempStages: any = {};
      settings.stages
        ?.filter((stage) => stage?.enabled === true)
        ?.forEach((stage) => {
          stage?.grades?.forEach((grade) => {
            grade?.sections?.forEach((section) => {
              tempStages[`${stage.id}$${grade.id}$${section}`] = {
                value: `${stage.id}$${grade.id}$${section}`,
                label: `${stage.name} - ${grade.name} - ${section}`,
              };
            });
          });
        });

      setStages(tempStages);
    }
  }, [settings]);
  function onEdit(teacher: any): void {
    form.setValue("full_name", teacher.full_name);
    form.setValue("email", teacher.email);
    setFieldValue("sections", teacher?.sections?.sections || []);
    setFormMode("edit");
    setEditingteacher(teacher);
    setIsDialogOpen(true);
  }

  async function handleEdit(teacher: any) {
    setLoadingProfiles(true);
    const { data, error } = await supabase.functions.invoke("update-user", {
      body: {
        full_name: form.getValues("full_name"),
        // email: form.getValues("email"),
        sections: values.sections,
        id: editingteacher.profile_id,
      },
    });
    // .eq("id", editingteacher.profile_id);
    // .select("*")
    // .single();
    if (error) {
      toast({
        title: "خطأ في التحديث",
        description: "حدث خطأ أثناء تحديث بيانات المعلم.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "تم التحديث بنجاح",
        description: `تم تحديث بيانات ${form.getValues("full_name")} بنجاح.`,
      });
      setIsDialogOpen(false);
      fetchProfiles();
    }
    setLoadingProfiles(false);
  }
  async function handleDelete(teacher: any) {
    const { data, error } = await supabase.functions.invoke("delete-user", {
      body: { id: teacher?.profile_id },
    });
    if (error) {
      toast({
        title: "خطأ في الحذف",
        description: "حدث خطأ أثناء حذف بيانات المعلم.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "تم الحذف بنجاح",
        description: `تم حذف بيانات ${teacher.full_name} بنجاح.`,
      });
      setEditingteacher(null);
      close();
      fetchProfiles();
    }
  }

  const columns: MRT_ColumnDef<any>[] = [
    {
      // accessorKey: "full_name",
      header: "#",
      Cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "full_name",
      header: "الاسم الكامل",
    },
    {
      accessorKey: "email",
      header: "البريد الإلكتروني",
    },
    {
      accessorKey: "sections",
      header: "الفصول",
      Cell: ({ cell, row }) => (
        <div className="flex flex-col gap-1">
          {(Array.isArray(cell.getValue<any>()?.sections)
            ? cell.getValue<any>()?.sections
            : []
          )?.map((section: string) => (
            <Badge key={section} className="bg-blue-100 text-blue-800">
              {stages[section]?.label || section}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: "الإجراءات",
      Cell: ({ cell, row }) => (
        <div className="flex flex-row gap-1">
          {" "}
          <Tooltip label="تعديل المعلم">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(row.original)}
              className="text-blue-600 hover:text-blue-800"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </Tooltip>{" "}
          <Tooltip label="حذف المعلم">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                open();
                setEditingteacher(row.original);
              }}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
    // {
    //   accessorKey: "city",
    //   header: "City",
    // },
    // {
    //   accessorKey: "state",
    //   header: "State",
    // },
  ];
  const table = useMantineReactTable({
    data: profiles,
    columns,
    mantineTableProps: {
      striped: false,
      border: 0,
    },
    mantinePaperProps: {
      shadow: "none", //use a larger shadow
      //customize paper styles
    },
    // enableRowSelection: true,
    initialState: {
      pagination: { pageSize: 5, pageIndex: 0 },
      showGlobalFilter: true,
    },
    //customize the MRT components
    mantinePaginationProps: {
      rowsPerPageOptions: ["5", "10", "15"],
    },
    paginationDisplayMode: "pages",
    state: {
      isLoading: loadingProfiles,
    },
    enableGlobalFilter: false,
  });

  return (
    <>
      <Modal opened={opened} onClose={close} title="حذف معلم" centered>
        {" "}
        هل أنت متأكد من رغبتك بحذف المعلم:{" "}
        <strong>{editingteacher?.full_name}</strong>?
        <Group mt="lg" justify="flex-end">
          <Button onClick={close} variant="default">
            إلغاء
          </Button>
          <Button onClick={() => handleDelete(editingteacher)} color="red">
            تأكيد الحذف
          </Button>
        </Group>
      </Modal>
      <Card className="bg-white/70 backdrop-blur-sm border-white/20" dir="rtl">
        <CardHeader className="bg-secondary text-white rounded-t-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-white text-lg">
                <Users className="h-5 w-5 text-white" />
                قائمة المعلمين{" "}
              </CardTitle>
              {/* <CardDescription className="text-sm text-white/90">
                إدارة بيانات الطلاب المسجلين
              </CardDescription> */}
            </div>
            <Button
              onClick={() => {
                form.reset();
                reset();
                setFormMode("create");
                setIsDialogOpen(true);
              }}
              className="flex items-center gap-2 w-full sm:w-auto bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
            >
              <Plus className="h-4 w-4" />
              إضافة معلم جديد
            </Button>
            <Modal
              opened={isDialogOpen}
              onClose={() => setIsDialogOpen(false)}
              title={formMode === "create" ? "إضافة معلم جديد" : "تعديل معلم"}
              centered
            >
              <Stack className="max-w-md m-auto">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(
                      formMode === "create" ? onSubmit : handleEdit
                    )}
                    className="space-y-4"
                  >
                    <FormField
                      control={form.control}
                      name="full_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>اسم المعلم</FormLabel>
                          <FormControl>
                            <Input placeholder="أحمد محمد علي" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {formMode !== "edit" && (
                      <>
                        <FormField
                          control={form.control}
                          name="email"
                          rules={{
                            required: "البريد الإلكتروني مطلوب",
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: "صيغة البريد الإلكتروني غير صحيحة",
                            },
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel> البريد الإلكتروني</FormLabel>
                              <FormControl>
                                <Input placeholder="" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="password"
                          rules={{
                            required: "كلمة المرور مطلوبة",
                            minLength: {
                              value: 8,
                              message: "كلمة المرور يجب أن تكون 8 أحرف على الأقل",
                            },
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center justify-between mb-2">
                                <FormLabel className="mb-0">كلمة المرور</FormLabel>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    form.setValue(
                                      "password",
                                      generateSimplePassword(),
                                      { shouldValidate: true }
                                    )
                                  }
                                >
                                  توليد كلمة مرور
                                </Button>
                              </div>
                              <FormControl>
                                <Input type="text" placeholder="********" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}
                    <form
                      onSubmit={onMantineFormSubmit((values) =>
                        console.log(values)
                      )}
                    >
                      <MultiSelect
                        label="الصفوف الدراسية"
                        data={Object.values(stages)}
                        // value={sections}
                        // name="sections"
                        // onChange={(val) => setSections(val)}
                        placeholder="اختر الصفوف"
                        // defaultValue={sections}
                        // searchable
                        key={formKey("sections")}
                        {...getInputProps("sections")}
                      />
                    </form>

                    <Button type="submit" className="w-full">
                      {formMode === "create" ? "إضافة معلم" : "تعديل معلم"}
                    </Button>
                  </form>
                </Form>
              </Stack>
            </Modal>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Stack>
            <MantineReactTable table={table} />
          </Stack>
        </CardContent>
      </Card>{" "}
    </>
  );
};
export default Teachers;
